/**
 * adaptExtraction - Adaptador ExtractionResult → Step1Page props
 * 
 * Convierte los datos del pipeline de extracción actual al formato
 * esperado por los componentes visuales de Step1Page.
 * 
 * @version 1.0.0
 */

import type { ExtractedInfrastructure, ProcessedInfrastructure, ExtractionStats, DocumentMetadata } from '../../../types/processing';
import type { NormalizationRow } from './NormalizationTable';
import type { DocumentContext, MunicipioPendienteDiagnostic, ZeroInfraDiagnostic } from './Step1Page';
import { buscarCandidatosMunicipio } from '../../../lib/codigosINEUnificado';
import { difierenSinCaja } from '../../../lib/nombreCaja';
import type { InfrastructureType } from '../shared/TypeBadge';
import { mapTipologia } from '../../../services/export/toProcessedRecord';
import type { TipologiaPTEL } from '../../../types/tipologia';
import { deriveProcedencia, deriveMotivoSinVia } from '../../../utils/procedenciaVia';


// ============================================================================
// TYPE MAPPING
// ============================================================================

/**
 * Mapea `tipo` + `nombre` del documento a `InfrastructureType` del Design System.
 *
 * TIPUNI (12-sep-2026, `DOP-TIPUNI-VOCABULARIO`=a, sellada por Luis): la cascada
 * vive en `mapTipologia` (`services/export/toProcessedRecord.ts`), en vocabulario
 * `TipologiaPTEL` (19), y es la misma que colorea P2 y exporta P4 — un elemento,
 * una tipología. Aquí solo se PROYECTA a los 9 tipos del DS; la inversa 9 → 19
 * no existe, por eso la función común no podía vivir en este vocabulario.
 * La cascada anterior de este fichero (tipo canónico → diccionario del ODT →
 * `classify` → fallback; `MAPTYPO`, `TIPCAD`, `TIPDIC`) está portada allí:
 * medido sobre 2 368 elementos PTEL (`p0_tipuni_20260912/diff_pre_post.txt`),
 * P1 cambia en 13: 8 ganados (`MUNICIPAL` con nombre mudo, «Correos»), 4
 * distintos por el diccionario de P2 (cementerio religioso, contraincendios
 * emergencias) y 1 «VERTEDERO» que pasa a RSU (= infraestructura aquí).
 */
function mapTypology(tipo: string, nombre = ''): InfrastructureType {
  return CLASSIFIER_TO_DS[mapTipologia(tipo, nombre)];
}

/**
 * Proyección `TipologiaPTEL` (19) → `InfrastructureType` del DS (9).
 * Total sobre los 19; exhaustiva por tipo.
 */
const CLASSIFIER_TO_DS: Record<TipologiaPTEL, InfrastructureType> = {
  SANITARIO:    'sanitario',
  EDUCATIVO:    'educativo',
  POLICIAL:     'policial',
  BOMBEROS:     'emergencias',
  EMERGENCIAS:  'emergencias',
  CULTURAL:     'cultural',
  RELIGIOSO:    'religioso',
  DEPORTIVO:    'deportivo',
  MUNICIPAL:    'administrativo',
  SOCIAL:       'administrativo',
  COMBUSTIBLE:  'infraestructura',
  HIDRAULICO:   'infraestructura',
  ENERGIA:      'infraestructura',
  TELECOM:      'infraestructura',
  VIAL:         'infraestructura',
  INDUSTRIAL:   'infraestructura',
  RSU:          'infraestructura',
  PUERTO:       'infraestructura',
  GENERICO:     'infraestructura',
};

/**
 * Parsea coordenada string a número
 */
function parseCoord(value: string | undefined | null): number | null {
  if (!value || value.trim() === '') return null;
  
  const cleaned = value
    .replace(/[^\d.,\-]/g, '')
    .replace(',', '.');
  
  const num = parseFloat(cleaned);
  return isNaN(num) || !isFinite(num) ? null : num;
}

// ============================================================================
// ADAPTERS
// ============================================================================

/**
 * Convierte ExtractedInfrastructure a NormalizationRow
 * 
 * P1-UI-3: Siempre preservar datos originales para vista comparativa.
 * Solo se muestran en la UI si hay diferencia con el valor normalizado.
 */
export function adaptInfrastructure(
  infra: ExtractedInfrastructure | ProcessedInfrastructure,
  index: number
): NormalizationRow {
  // B02: Usar coordenadas finales del pipeline cuando estén disponibles.
  // ProcessedInfrastructure.xFinal/yFinal contienen el valor ya corregido
  // (swap X↔Y, conversión WGS84→UTM30, corrección ED50...).
  // ExtractedInfrastructure (sin pipeline) cae a xOriginal/yOriginal.
  const x = ('xFinal' in infra && infra.xFinal !== undefined)
    ? infra.xFinal
    : parseCoord(infra.xOriginal);
  const y = ('yFinal' in infra && infra.yFinal !== undefined)
    ? infra.yFinal
    : parseCoord(infra.yOriginal);
  
  // Score real del pipeline si está disponible (ProcessedInfrastructure.score),
  // fallback a heurística básica: 70 si tiene coords válidas, null si no.
  const pipelineScore = 'score' in infra ? infra.score : null;
  const hasValidCoords = x !== null && y !== null;
  const heuristicScore = hasValidCoords ? 70 : null;
  const newScore = pipelineScore ?? heuristicScore;
  // SCFL (10-sep-2026, OBS-P1-SCORE-FLECHA-ENGANOSA): la heurística NO es un score
  // previo del elemento, es un literal. B05-FIX (7a6770e0, 10-abr) la propagaba
  // como originalScore siempre que el pipeline corría, y la tabla de P1 pintaba
  // «70 → N» en todas las filas con coordenadas: un 70 que no discrimina y una
  // flecha que sugiere una trayectoria descendente que nunca ocurrió. La celda
  // pinta ahora solo el score real; originalScore queda reservado a un valor que
  // provenga del registro (hoy ninguno en P1), nunca a la heurística.
  const originalScore = undefined;
  
  // P1-UI-3: Determinar valores originales para vista comparativa
  // Solo establecer originalName/originalAddress si hay diferencia con el valor normalizado
  const originalName = infra.nombreOriginal && infra.nombreOriginal !== infra.nombre
    ? infra.nombreOriginal
    : undefined;
  
  // P1-UI-3b: Usar campo direccionOriginal tipado
  // DIRCAJA: la caja y las tildes solas no cuentan como diferencia (el «Orig:» no repite la fila).
  const originalAddress = infra.direccionOriginal && difierenSinCaja(infra.direccionOriginal, infra.direccion)
    ? infra.direccionOriginal
    : undefined;
  
  // P1-S4 (§B): vía de resolución por fila + motivo del residuo sin geo.
  // Derivación declarativa de source/normalization — no toca el pipeline.
  const procedencia = deriveProcedencia(infra, hasValidCoords);
  const motivoSinVia = procedencia === 'sin_geo'
    ? deriveMotivoSinVia(infra)
    : undefined;

  return {
    id: index + 1,
    name: infra.nombre,
    originalName,
    address: infra.direccion || undefined,
    originalAddress,
    // AFOROP1: el aforo recortado del nombre por el extractor va a la segunda línea de la fila
    aforo: infra.aforoDeclarado,
    aforoUnidad: infra.aforoUnidad,
    type: mapTypology(infra.tipo, infra.nombre),
    x,
    y,
    originalScore,
    newScore,
    procedencia,
    motivoSinVia,
  };
}

/**
 * Convierte array de ExtractedInfrastructure a NormalizationRow[]
 */
export function adaptInfrastructures(
  infrastructures: (ExtractedInfrastructure | ProcessedInfrastructure)[]
): NormalizationRow[] {
  return infrastructures.map(adaptInfrastructure);
}

/**
 * Convierte DocumentMetadata a DocumentContext
 */
export function adaptMetadata(metadata: DocumentMetadata): DocumentContext {
  return {
    fileName: metadata.fileName,
    municipio: metadata.municipality || 'Sin municipio',
    provincia: metadata.province || 'Sin provincia',
    ine: metadata.ineCode || '00000',
  };
}

/**
 * Extrae métricas de ExtractionStats
 */
export function adaptStats(stats: ExtractionStats): {
  geolocatedCount: number;
  totalCount: number;
} {
  return {
    geolocatedCount: stats.withCoordinates,
    totalCount: stats.total,
  };
}

/**
 * P1-S2 (esc. 3): construye el diagnóstico cero-infra a partir de los
 * metadatos que el extractor ya expone. Municipio/provincia/INE vienen
 * de la detección por nombre de archivo; pueden faltar (undefined).
 */
export function adaptZeroInfraDiagnostic(
  fileName: string,
  metadata: DocumentMetadata
): ZeroInfraDiagnostic {
  return {
    fileName,
    fileType: metadata.fileType,
    municipio: metadata.municipality,
    provincia: metadata.province,
    ine: metadata.ineCode,
  };
}

/**
 * P1-S3 (esc. 4): construye el diagnóstico «municipio sin confirmar».
 * Si hubo detección de nombre pero sin correspondencia INE, propone
 * homónimos/próximos del catálogo de los 785 (caso Colomers → Colomera).
 * NO propaga metadata.province: proviene de detectProvince(), cuyo
 * fallback mudo a «Granada» enmascararía el problema que este gate
 * existe para destapar. La provincia sale del catálogo, por candidato.
 */
export function adaptMunicipioPendiente(
  fileName: string,
  metadata: DocumentMetadata
): MunicipioPendienteDiagnostic {
  const detectado = metadata.municipality;
  return {
    fileName,
    municipioDetectado: detectado,
    candidatos: detectado ? buscarCandidatosMunicipio(detectado) : [],
  };
}

/**
 * Adaptador completo: ExtractionResult → Step1Page props
 */
export interface AdaptedStep1Props {
  documentContext: DocumentContext;
  data: NormalizationRow[];
  geolocatedCount: number;
  totalCount: number;
}

export function adaptExtractionResult(result: {
  infrastructures: ExtractedInfrastructure[];
  stats: ExtractionStats;
  metadata: DocumentMetadata;
}): AdaptedStep1Props {
  return {
    documentContext: adaptMetadata(result.metadata),
    data: adaptInfrastructures(result.infrastructures),
    ...adaptStats(result.stats),
  };
}
